import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getLandingPageTitleColor } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./dragDropStage.css";
import "./crosswordStage.css";
import "./stageActions.css";
import { parseDosDontFromTheme, shuffleArray } from "./dosDontUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
  updateExternalReport,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";
const STAGE_NUM = 5;
const MAX_ZONE_SLOTS = 5;
const POOL_VISIBLE_MAX = 5;

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function StatementCard({
  item,
  zone,
  locked,
  onPointerDragStart,
  isDragging,
  textStyle,
}) {
  const correct =
    zone === "do" ? item.type === "do" : zone === "dont" ? item.type === "dont" : null;
  const mod =
    zone === "pool"
      ? ""
      : correct
        ? " dragdrop-stage__card--placed-correct"
        : " dragdrop-stage__card--placed-wrong";

  return (
    <div
      className={`dragdrop-stage__card${mod}${
        locked ? " dragdrop-stage__card--locked" : ""
      }${isDragging ? " dragdrop-stage__card--dragging" : ""}`}
      onPointerDown={
        locked ? undefined : (e) => onPointerDragStart(e, item.id)
      }
      style={textStyle}
    >
      {item.text}
    </div>
  );
}

function resolveDropZone(clientX, clientY) {
  const el = document.elementFromPoint(clientX, clientY);
  if (!el) return null;
  const zoneEl = el.closest("[data-drop-zone]");
  const zone = zoneEl?.getAttribute("data-drop-zone");
  return zone === "do" || zone === "dont" || zone === "pool" ? zone : null;
}

function scrollZoneToBottom(zone) {
  if (zone !== "do" && zone !== "dont") return;
  requestAnimationFrame(() => {
    const body = document.querySelector(
      `[data-drop-zone="${zone}"] .dragdrop-stage__zone-body`
    );
    if (body) body.scrollTop = body.scrollHeight;
  });
}

export default function Stage5() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage5Points, setStage5Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [placements, setPlacements] = useState({});
  const [poolOrder, setPoolOrder] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [activeDragId, setActiveDragId] = useState(null);
  const [ghostPos, setGhostPos] = useState({ x: 0, y: 0 });

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const stage5PointsRef = useRef(0);
  const scoredIdsRef = useRef(new Set());
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const dragIdRef = useRef(null);
  const placementsRef = useRef({});
  const itemsByIdRef = useRef({});
  const boardInitializedRef = useRef(false);
  const pointerDragCleanupRef = useRef(null);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const { dos, donts, all: allItems } = useMemo(
    () => parseDosDontFromTheme(themeData),
    [themeData]
  );

  const pointsPerCorrect = useMemo(() => {
    const p = Number(themeData?.points);
    return Number.isFinite(p) && p > 0 ? p : 10;
  }, [themeData?.points]);

  const maxStage5Score = allItems.length * pointsPerCorrect;

  const title = useMemo(() => {
    const t =
      themeData?.dos_dont_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      "BUILDING YOUR BRAND";
    return String(t).trim().toUpperCase();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom = themeData?.dos_dont_subtitle;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "Identify the do's and don'ts from the statements below in building your strong brand presence.";
  }, [themeData]);

  const titleTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );

  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  useEffect(() => {
    itemsByIdRef.current = Object.fromEntries(allItems.map((i) => [i.id, i]));
  }, [allItems]);

  useEffect(() => {
    placementsRef.current = placements;
  }, [placements]);

  useEffect(() => {
    return () => {
      pointerDragCleanupRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("dragdrop-page");
    return () => {
      document.body.classList.remove("dragdrop-page");
    };
  }, []);

  useEffect(() => {
    if (!allItems.length || boardInitializedRef.current) return;
    boardInitializedRef.current = true;
    const shuffled = shuffleArray(allItems.map((i) => i.id));
    setPoolOrder(shuffled);
    const init = {};
    shuffled.forEach((id) => {
      init[id] = "pool";
    });
    setPlacements(init);
  }, [allItems]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage5Points);
  }, [stage5Points]);

  const doIds = useMemo(
    () => allItems.filter((i) => placements[i.id] === "do").map((i) => i.id),
    [allItems, placements]
  );
  const dontIds = useMemo(
    () => allItems.filter((i) => placements[i.id] === "dont").map((i) => i.id),
    [allItems, placements]
  );

  const poolItems = useMemo(() => {
    const waiting = poolOrder.filter((id) => placements[id] === "pool");
    return waiting.slice(0, POOL_VISIBLE_MAX);
  }, [poolOrder, placements]);

  const allPlaced =
    allItems.length > 0 &&
    allItems.every((item) => placements[item.id] !== "pool");

  const finishStage = useCallback(
    async (finalStage5Score, skip = false) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);
      const stage5Score =
        skip || finalStage5Score == null
          ? 0
          : finalStage5Score;
      const finalTotal = otherStagesTotalRef.current + stage5Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        try {
          await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            stageNumber: STAGE_NUM,
            score: stage5Score,
            time: timeStr,
            total_score: finalTotal,
          });
          await fetch(`${backendBase}/welcomeStageComplete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              stageId,
              time: timeStr,
              total_score: finalTotal,
              points: finalTotal,
            }),
          });
          await updateExternalReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            points: finalTotal,
            time: timeStr,
            stageId,
          });
        } catch (err) {
          console.error("Stage5 finish:", err);
        }
      }

      clearStageActive(5);
      markStageCompleted(5, storedUser);

      await showStageCompleteCelebration({
        stageNumber: 5,
        skip,
        score: stage5Score,
      });

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigate("/leaderboard");
    },
    [
      submitting,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      navigate,
    ]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded" || !allItems.length) return;

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st, {
            maxStageScore: maxStage5Score,
          });
          const resumeRoute = getRedirectForPlayedStage(st, 5, {
            maxStageScore: maxStage5Score,
          });
          if (resumeRoute) {
            submittedRef.current = true;
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage5 = Number(st.stage5) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage5);
          stage5PointsRef.current = dbStage5;
          setStage5Points(dbStage5);
          setDisplayTotalScore(dbTotal);

          if (pointsPerCorrect > 0 && dbStage5 > 0) {
            const solvedCount = Math.min(
              allItems.length,
              Math.floor(dbStage5 / pointsPerCorrect)
            );
            const guessed = new Set(
              allItems.slice(0, solvedCount).map((i) => i.id)
            );
            scoredIdsRef.current = guessed;
          }
        }

        setStageActive(5, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage5 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    allItems,
    allItems.length,
    maxStage5Score,
    pointsPerCorrect,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    navigate,
  ]);

  const endPointerDrag = useCallback(() => {
    pointerDragCleanupRef.current?.();
    pointerDragCleanupRef.current = null;
    dragIdRef.current = null;
    setActiveDragId(null);
    setDragOver(null);
    document.body.classList.remove("dragdrop-stage--dragging");
  }, []);

  const applyDrop = useCallback(
    (targetZone) => {
      const id = dragIdRef.current;
      if (!id || !itemsByIdRef.current[id]) return;

      const item = itemsByIdRef.current[id];
      const prevZone = placementsRef.current[id] || "pool";
      if (prevZone !== "pool") return;
      if (prevZone === targetZone) return;
      if (targetZone === "pool") return;

      setPlacements((prev) => ({ ...prev, [id]: targetZone }));
      scrollZoneToBottom(targetZone);
      const correctZone = item.type === "do" ? "do" : "dont";
      if (targetZone === correctZone && !scoredIdsRef.current.has(id)) {
        scoredIdsRef.current.add(id);
        stage5PointsRef.current += pointsPerCorrect;
        setStage5Points(stage5PointsRef.current);
      }
    },
    [pointsPerCorrect]
  );

  const handlePointerDragStart = useCallback(
    (e, id) => {
      if (submitting) return;
      if ((placementsRef.current[id] || "pool") !== "pool") return;
      if (e.pointerType === "mouse" && e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      pointerDragCleanupRef.current?.();

      dragIdRef.current = id;
      setActiveDragId(id);
      setGhostPos({ x: e.clientX, y: e.clientY });
      setDragOver(resolveDropZone(e.clientX, e.clientY));
      document.body.classList.add("dragdrop-stage--dragging");

      const onMove = (ev) => {
        if (ev.pointerId !== e.pointerId) return;
        ev.preventDefault();
        setGhostPos({ x: ev.clientX, y: ev.clientY });
        setDragOver(resolveDropZone(ev.clientX, ev.clientY));
      };

      const onEnd = (ev) => {
        if (ev.pointerId !== e.pointerId) return;
        ev.preventDefault();
        const zone =
          resolveDropZone(ev.clientX, ev.clientY) ||
          placementsRef.current[id] ||
          "pool";
        applyDrop(zone);
        endPointerDrag();
      };

      document.addEventListener("pointermove", onMove, { passive: false });
      document.addEventListener("pointerup", onEnd);
      document.addEventListener("pointercancel", onEnd);

      pointerDragCleanupRef.current = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onEnd);
        document.removeEventListener("pointercancel", onEnd);
      };
    },
    [submitting, applyDrop, endPointerDrag]
  );

  const handleSubmit = useCallback(() => {
    finishStage(stage5PointsRef.current, false);
  }, [finishStage]);

  const handleSkip = useCallback(() => {
    scoredIdsRef.current = new Set();
    stage5PointsRef.current = 0;
    setStage5Points(0);
    finishStage(0, true);
  }, [finishStage]);

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "dos_dont_rules"),
      themeData,
    });
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return (
      <div className="dragdrop-stage">
        <p className="dragdrop-stage__loading">Loading game…</p>
      </div>
    );
  }

  if (!allItems.length) {
    return (
      <div className="dragdrop-stage">
        <p className="dragdrop-stage__empty">
          No do&apos;s and don&apos;ts configured. Add statements in theme admin
          under Dos and Dont.
        </p>
      </div>
    );
  }

  const activeDragItem = activeDragId
    ? itemsByIdRef.current[activeDragId]
    : null;

  return (
    <div className={`dragdrop-stage${activeDragId ? " dragdrop-stage--active-drag" : ""}`}>
      {activeDragItem && (
        <div
          className="dragdrop-stage__drag-ghost"
          style={{ left: ghostPos.x, top: ghostPos.y, ...themedTextStyle }}
          aria-hidden
        >
          {activeDragItem.text}
        </div>
      )}

      <header className="dragdrop-stage__header">
        <div className="dragdrop-stage__title-block">
          <span className="dragdrop-stage__badge">STAGE 5</span>
          <h1 className="dragdrop-stage__title" style={themedTextStyle}>
            {title}
          </h1>
          <p className="dragdrop-stage__subtitle" style={themedTextStyle}>
            {subtitle}
          </p>
        </div>
        <div className="dragdrop-stage__hud">
          <div className="dragdrop-stage__hud-card">
            <div className="dragdrop-stage__hud-stat">
              <span aria-hidden>⏱</span>
              <span>{formatSecondsToClock(elapsedSec)}</span>
            </div>
            <div className="dragdrop-stage__hud-stat">
              <span aria-hidden style={{ color: "#f5c842" }}>
                ★
              </span>
              <span>{displayTotalScore}</span>
            </div>
          </div>
          <div className="stage-hud-actions">
            <button
              type="button"
              className="crossword-stage__help-btn"
              aria-label="How to play"
              onClick={showHelp}
            >
              ?
            </button>
            <button
              type="button"
              className="game-stage-actions__skip-btn game-stage-actions__skip-btn--hud"
              disabled={submitting}
              onClick={handleSkip}
            >
              Skip
            </button>
          </div>
        </div>
      </header>

      <div
        className="dragdrop-stage__board"
        style={{
          "--dd-max-slots": MAX_ZONE_SLOTS,
          "--dd-pool-visible": POOL_VISIBLE_MAX,
        }}
      >
        <div
          className={`dragdrop-stage__zone${
            dragOver === "do" ? " dragdrop-stage__zone--drag-over" : ""
          }`}
          data-drop-zone="do"
        >
          <div className="dragdrop-stage__zone-header dragdrop-stage__zone-header--do">
            DO&apos;S
          </div>
          <div
            className={`dragdrop-stage__zone-body${
              dragOver === "do" ? " dragdrop-stage__zone-body--drag-over" : ""
            }${doIds.length > MAX_ZONE_SLOTS ? " dragdrop-stage__zone-body--filled" : ""}`}
          >
            {doIds.map((id) => (
              <StatementCard
                key={id}
                item={itemsByIdRef.current[id]}
                zone="do"
                locked
                isDragging={activeDragId === id}
                onPointerDragStart={handlePointerDragStart}
                textStyle={themedTextStyle}
              />
            ))}
          </div>
        </div>

        <div
          className={`dragdrop-stage__zone${
            dragOver === "dont" ? " dragdrop-stage__zone--drag-over" : ""
          }`}
          data-drop-zone="dont"
        >
          <div className="dragdrop-stage__zone-header dragdrop-stage__zone-header--dont">
            DON&apos;TS
          </div>
          <div
            className={`dragdrop-stage__zone-body${
              dragOver === "dont"
                ? " dragdrop-stage__zone-body--drag-over"
                : ""
            }${
              dontIds.length > MAX_ZONE_SLOTS
                ? " dragdrop-stage__zone-body--filled"
                : ""
            }`}
          >
            {dontIds.map((id) => (
              <StatementCard
                key={id}
                item={itemsByIdRef.current[id]}
                zone="dont"
                locked
                isDragging={activeDragId === id}
                onPointerDragStart={handlePointerDragStart}
                textStyle={themedTextStyle}
              />
            ))}
          </div>
        </div>

        <div
          className={`dragdrop-stage__pool${
            dragOver === "pool" ? " dragdrop-stage__pool--drag-over" : ""
          }`}
          data-drop-zone="pool"
        >
          <div className="dragdrop-stage__pool-inner">
            {poolItems.map((id) => (
              <StatementCard
                key={id}
                item={itemsByIdRef.current[id]}
                zone="pool"
                isDragging={activeDragId === id}
                onPointerDragStart={handlePointerDragStart}
                textStyle={themedTextStyle}
              />
            ))}
          </div>
          <span className="dragdrop-stage__pool-label">DRAG AND DROP</span>
        </div>
      </div>

      <div className="dragdrop-stage__footer">
        <button
          type="button"
          className="dragdrop-stage__btn dragdrop-stage__btn--primary"
          disabled={submitting || !allPlaced}
          onClick={handleSubmit}
        >
          {submitting ? "Saving…" : "Submit"}
        </button>
      </div>
    </div>
  );
}
